import { useCallback, useEffect, useRef, useState } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft, Clock, Loader2, MapPin, Pause, Play, Phone, Ruler, FileDown, Printer,
  CheckCircle2, Plus, Users2, Trash2, Receipt,
} from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import {
  api, apiError, WORKFLOW, clock, timeOf, dateTimeOf, dateOf, fmtDuration, getToken, isLeader,
} from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import NavigateButton from "@/components/NavigateButton";
import PhotoGrid from "@/components/PhotoGrid";
import LineItems, { sumItems } from "@/components/LineItems";
import InvoicePanel from "@/components/InvoicePanel";
import FinishedEditPanel from "@/components/FinishedEditPanel";
import StatusChangeButton from "@/components/StatusChangeButton";
import { MyTimePanel } from "@/components/MyTimePanel";
import { TimeEntriesPanel } from "@/components/TimeEntriesPanel";
import { JobDialog } from "@/components/RecordDialogs";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

const tone = {
  slate: "bg-slate-100 text-slate-700 border-slate-200",
  blue: "bg-blue-50 text-blue-700 border-blue-200",
  indigo: "bg-indigo-50 text-indigo-700 border-indigo-200",
  green: "bg-green-50 text-green-700 border-green-200",
  orange: "bg-orange-50 text-orange-800 border-orange-200",
  teal: "bg-teal-50 text-teal-700 border-teal-200",
};

const secsBetween = (a, b) => (a && b ? Math.max(0, (new Date(b) - new Date(a)) / 1000) : 0);

const liveWork = (w, nowMs) => {
  const accum = w?.work_accum_seconds || 0;
  if (!w?.work_start) return accum;
  const end = w.work_end ? new Date(w.work_end).getTime() : nowMs;
  const gross = Math.max(0, (end - new Date(w.work_start).getTime()) / 1000);
  const pauses = w.pauses || [];
  let paused = pauses.reduce((s, p) => s + (p.seconds || 0), 0);
  const open = pauses.find((p) => !p.end);
  if (open) paused += Math.max(0, (end - new Date(open.start).getTime()) / 1000);
  return accum + Math.max(0, gross - paused);
};

const Row = ({ label, value, testid }) => (
  <div className="flex items-center justify-between border-b border-slate-100 py-2 last:border-0">
    <span className="text-xs font-medium uppercase tracking-[0.05em] text-slate-500">{label}</span>
    <span className="text-sm font-medium text-slate-900" data-testid={testid}>{value}</span>
  </div>
);

export default function WorkOrder() {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, company } = useAuth();
  const ro = !!company?.demo_readonly;
  const leader = isLeader(user?.role) && !ro;
  const [job, setJob] = useState(null);
  const [summary, setSummary] = useState(null);
  const [busy, setBusy] = useState(false);
  const [now, setNow] = useState(Date.now());
  const [noteText, setNoteText] = useState("");
  const [pausing, setPausing] = useState(false);
  const fileRef = useRef(null);
  const imageRef = useRef(null);
  const docRef = useRef(null);
  const [editOpen, setEditOpen] = useState(false);
  const [invoiceConfirm, setInvoiceConfirm] = useState(false);
  const [staff, setStaff] = useState([]);
  const token = getToken();

  const load = useCallback(async () => {
    try {
      const [j, s] = await Promise.all([api.get(`/jobs/${id}`), api.get(`/jobs/${id}/summary`)]);
      setJob(j.data);
      setSummary(s.data);
    } catch (e) {
      toast.error(apiError(e));
      navigate("/jobb");
    }
  }, [id, navigate]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/users").then(({ data }) => setStaff(data)).catch(() => {});
  }, []);
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  const act = async (action) => {    setBusy(true);
    try {
      await api.post(`/jobs/${id}/workflow`, { action });
      await load();
    } catch (e) {
      toast.error(apiError(e));
      load();
    } finally {
      setBusy(false);
    }
  };

  const markInvoiced = async () => {
    setBusy(true);
    try {
      await api.post(`/jobs/${id}/invoiced`);
      setInvoiceConfirm(false);
      toast.success(t("wo.invoicedDone"));
      await load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const markReady = async () => {
    if (!window.confirm(t("wo.readyConfirm"))) return;
    setBusy(true);
    try {
      await api.post(`/jobs/${id}/ready-to-invoice`);
      toast.success(t("wo.readySaved"));
      await load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const addNote = async () => {
    if (!noteText.trim()) return;
    try {
      const { data } = await api.post(`/jobs/${id}/notes`, { text: noteText.trim(), phase: "during" });
      setJob(data);
      setNoteText("");
      toast.success(t("wo.noteSaved"));
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const removeNote = async (noteId) => {
    try {
      const { data } = await api.delete(`/jobs/${id}/notes/${noteId}`);
      setJob(data);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const saveItems = async (key, items) => {
    setJob((j) => ({ ...j, [key]: items }));
    try {
      await api.put(`/jobs/${id}`, { [key]: items });
    } catch (e) {
      toast.error(apiError(e));
      load();
    }
  };

  const uploadPhotos = async (files, ref) => {
    if (!files?.length) return;
    setBusy(true);
    try {
      for (const file of files) {
        const form = new FormData();
        form.append("file", file);
        const { data } = await api.post(`/jobs/${id}/photos`, form, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        setJob(data);
      }
      toast.success(t("photos.photoSaved"));
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
      if (ref?.current) ref.current.value = "";
    }
  };

  const upload = (e) => uploadPhotos(Array.from(e.target.files || []), fileRef);
  const uploadFromGallery = (e) => uploadPhotos(Array.from(e.target.files || []), imageRef);

  const uploadDocs = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setBusy(true);
    try {
      for (const file of files) {
        const form = new FormData();
        form.append("file", file);
        const { data } = await api.post(`/jobs/${id}/files`, form, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        setJob(data);
      }
      toast.success(t("photos.fileSaved"));
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
      if (docRef.current) docRef.current.value = "";
    }
  };

  const removeDoc = async (fileId) => {
    if (!window.confirm(t("photos.deleteFile"))) return;
    try {
      const { data } = await api.delete(`/jobs/${id}/files/${fileId}`);
      setJob(data);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const removePhoto = async (photoId) => {
    if (!window.confirm(t("photos.deletePhoto"))) return;
    try {
      const { data } = await api.delete(`/jobs/${id}/photos/${photoId}`);
      setJob(data);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const pdfUrl = `${process.env.REACT_APP_BACKEND_URL}/api/jobs/${id}/pdf?auth=${token}`;
  const openPdf = () => {
    const a = document.createElement("a");
    a.href = pdfUrl;
    a.target = "_blank";
    a.rel = "noopener noreferrer";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  if (!job) return <p className="p-4 text-sm text-slate-500">{t("wo.loadingOrder")}</p>;

  const workshop = job.job_type === "workshop";
  const openJob = job.job_type === "open";
  const myWorkflow = (job.workflows || []).find((w) => w.user_id === user?.id);
  const me = myWorkflow || { status: "not_started", pauses: [] };
  const status = me.status || "not_started";
  // Användare utan egen tidtagning (t.ex. chefen) ser orderns status i rubriken
  const headerStatus = myWorkflow ? status : (job.workflow_status || job.status || "not_started");
  const wf = WORKFLOW[headerStatus] || WORKFLOW[status];
  const paused = status === "paused";
  const working = status === "working";
  const workSecs = pausing ? liveWork(me, new Date(me.pauses?.find((p) => !p.end)?.start || now).getTime()) : liveWork(me, now);
  const pauseSecs =
    (me.pause_accum_seconds || 0) +
    (me.pauses || []).reduce((s, p) => s + (p.seconds || 0), 0) +
    (paused ? secsBetween((me.pauses || []).find((p) => !p.end)?.start, new Date(now).toISOString()) : 0);
  const travelOut = status === "travel_out"
    ? secsBetween(me.travel_start, new Date(now).toISOString())
    : me.travel_out_seconds || 0;
  const photos = (job.photos || []).filter((p) => !p.is_deleted);
  const files = (job.files || []).filter((f) => !f.is_deleted);
  const done = status === "completed" || job.status === "ready_to_invoice";
  if (job.job_type === "installation") {
    return <Navigate to={`/nymontering/${id}`} replace />;
  }
  const readyToInvoice = !!job.ready_to_invoice;
  const invoiced = !!job.invoiced;
  const jobPaused = status === "job_paused";
  const orderNo = job.order_number ? String(job.order_number).padStart(4, "0") : "–";

  // Huvudknappens åtgärd: verkstad och öppen order kan starta arbetet direkt
  const skipTravel = workshop || openJob;
  const mainAction = status === "not_started" && skipTravel ? "start_work" : wf.action;
  const mainLabel = status === "not_started" && skipTravel ? t("wo.startWork") : t(`wfBtn.${status}`);
  const showMain = !done && !invoiced && !job.locked && !(working || paused);
  const showTravelOption = openJob && status === "not_started";
  const finished = done || job.locked;
  const canCorrect = leader && (job.workflows || []).length >= 0;

  const totals = summary?.totals || { work_seconds: 0, travel_seconds: 0, total_seconds: 0 };

  return (
    <div className="mx-auto w-full max-w-2xl xl:max-w-5xl" data-testid="work-order-page">
      <button
        onClick={() => navigate(-1)}
        data-testid="work-order-back"
        className="mb-3 inline-flex items-center gap-1.5 text-sm font-medium text-slate-600 transition-colors duration-200 hover:text-slate-900"
      >
        <ArrowLeft className="h-4 w-4" /> {t("common.back")}
      </button>

      <div className="rounded-lg border border-slate-200 bg-white p-4">
        <div className="flex items-start gap-2">
          <span className="mt-0.5 shrink-0 rounded bg-slate-900 px-2 py-1 font-heading text-sm font-bold text-white" data-testid="work-order-number">
            #{orderNo}
          </span>
          <h1 className="min-w-0 flex-1 text-2xl font-bold text-slate-900">{job.title}</h1>
          {leader && <StatusChangeButton job={job} onSaved={load} />}
        </div>
        <span
          data-testid="work-order-status"
          className={`mt-2 inline-block rounded-md border px-2 py-1 text-xs font-semibold uppercase tracking-[0.05em] ${
            invoiced ? "bg-indigo-50 text-indigo-700 border-indigo-200" : tone[wf.tone]
          }`}
        >
          {t("wo.orderStatus")}: {invoiced ? t("wf.invoiced") : readyToInvoice ? t("wf.ready_to_invoice") : t(`wf.${headerStatus}`)}
        </span>

        <div className="mt-2 space-y-1 text-sm text-slate-600">
          {job.customer_name && <p className="font-medium text-slate-800">{job.customer_name}</p>}
          {job.customer_address && <p>{job.customer_address}</p>}
          <p className="flex items-center gap-2"><Clock className="h-4 w-4 text-slate-400" /> {t("wo.planned")} {dateTimeOf(job.start)}
            {job.end && <span data-testid="work-order-end">– {job.end.slice(0, 10) === job.start?.slice(0, 10) ? job.end.slice(11, 16) : dateTimeOf(job.end)}</span>}</p>
          <p className="text-xs font-medium uppercase tracking-[0.05em] text-slate-500" data-testid="work-order-type">
            {workshop ? t("wo.workshopJob") : openJob ? t("wo.openOrder") : t("wo.outdoorJob")}
          </p>
          {job.edited_after_finish && (
            <p
              data-testid="work-order-edited-badge"
              className="inline-flex rounded-md border border-amber-300 bg-amber-50 px-2 py-1 text-xs font-semibold uppercase tracking-[0.05em] text-amber-800"
            >
              {t("wo.editedAfterFinish")}
            </p>
          )}
          {job.description && <p className="pt-1 text-slate-700">{job.description}</p>}
        </div>

        {job.customer_phone && (
          <a
            data-testid="work-order-call"
            href={`tel:${job.customer_phone.replace(/\s/g, "")}`}
            className="mt-3 inline-flex h-11 items-center gap-2 rounded-md bg-green-600 px-4 text-sm font-semibold text-white transition-colors duration-200 hover:bg-green-700"
          >
            <Phone className="h-4 w-4" /> {t("wo.call")} {job.customer_phone}
          </a>
        )}

        {!workshop && job.address && (
          <div className="mt-3 rounded-md border border-slate-200 bg-amber-50/50 p-3">
            <p className="flex items-start gap-2 text-sm font-medium text-slate-900" data-testid="work-order-address">
              <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-blue-600" /> {job.address}
            </p>
            {job.distance_km != null && (
              <p className="mt-1 flex items-center gap-1.5 text-sm text-slate-600" data-testid="work-order-distance">
                <Ruler className="h-3.5 w-3.5" /> {t("wo.distance", { single: (job.distance_km / 10).toFixed(1) })}{" "}
                <b>{(job.distance_km / 5).toFixed(1)} mil</b>
              </p>
            )}
            <div className="mt-2">
              <NavigateButton address={job.address} testid="work-order-navigate" />
            </div>
          </div>
        )}
      </div>

      {/* Live status + knappar (ej för öppen order) */}
      {openJob ? (
        <div
          className="mt-3 rounded-lg border border-slate-200 bg-blue-50/60 p-4 text-sm font-medium text-slate-700"
          data-testid="work-order-open-info"
        >
          {t("wo.openInfoOrder")}
        </div>
      ) : (
      <div className="mt-3 rounded-lg border border-slate-200 bg-white p-4">
        <div className="flex flex-wrap items-center gap-2">
          <span
            className={`h-2.5 w-2.5 rounded-full ${
              working ? "animate-pulse bg-green-500" : paused ? "bg-orange-500" : done ? "bg-slate-400" : "bg-blue-500"
            }`}
          />
          <span className="text-sm font-semibold uppercase tracking-[0.05em] text-slate-800">{t(`wf.${status}`)}</span>
        </div>

        {!done ? (
          <div className="mt-3 grid grid-cols-2 gap-2">
            <div className="rounded-md border border-slate-200 px-3 py-2">
              <p className="text-xs font-medium uppercase tracking-[0.05em] text-slate-500">{t("wo.workTime")}</p>
              <p className="font-heading text-lg font-bold tabular-nums text-slate-900" data-testid="work-order-work-timer">
                {clock(workSecs)}
              </p>
            </div>
            <div className="rounded-md border border-slate-200 px-3 py-2">
              <p className="text-xs font-medium uppercase tracking-[0.05em] text-slate-500">{t("wo.pauseTime")}</p>
              <p className="font-heading text-lg font-bold tabular-nums text-slate-900" data-testid="work-order-pause-timer">
                {clock(pauseSecs)}
              </p>
            </div>
          </div>
        ) : (
          <div className="mt-3 rounded-md bg-slate-50 p-3" data-testid="work-order-final-values">
            <Row label={t("wo.finalWork")} value={fmtDuration(me.work_seconds || 0)} testid="final-work" />
            <Row label={t("wo.finalPause")} value={fmtDuration(me.pause_seconds || 0)} testid="final-pause" />
            <Row
              label={t("wo.finalTravel")}
              value={fmtDuration((me.travel_out_seconds || 0) + (me.travel_home_seconds || 0))}
              testid="final-travel"
            />
          </div>
        )}

        {!done && (me.travel_start || travelOut > 0) && (
          <div className="mt-3 rounded-md bg-slate-50 p-3">
            <Row label={t("wo.travelStart")} value={timeOf(me.travel_start)} testid="work-order-travel-start" />
            <Row label={t("wo.arrival")} value={timeOf(me.arrived_at)} testid="work-order-arrived" />
            <Row label={t("wo.travelOut")} value={fmtDuration(travelOut)} testid="work-order-travel-out" />
          </div>
        )}

        {!done && !ro && !invoiced ? (
          <div className="mt-4 space-y-2">
            {showMain && (
              <Button
                data-testid="work-order-main-button"
                disabled={busy}
                onClick={() => act(mainAction)}
                className="h-20 w-full whitespace-normal px-3 font-heading text-lg font-bold leading-tight tracking-wide text-white transition-colors duration-200 bg-blue-600 hover:bg-blue-700 sm:text-xl"
              >
                {busy ? <Loader2 className="h-6 w-6 animate-spin" /> : mainLabel}
              </Button>
            )}

            {showTravelOption && (
              <Button
                data-testid="work-order-start-travel-option"
                disabled={busy}
                onClick={() => act("start_travel")}
                variant="outline"
                className="h-14 w-full border-slate-300 bg-white font-heading text-base font-bold tracking-wide text-slate-900 transition-colors duration-200 hover:bg-slate-100"
              >
                {t("wo.startTravelOptional")}
              </Button>
            )}

            {(working || paused) && (
              <div className="grid grid-cols-2 gap-2">
                <Button
                  data-testid="work-order-work-button"
                  disabled
                  className="h-20 whitespace-normal bg-green-600 font-heading text-base font-bold text-white opacity-100 disabled:opacity-100 sm:text-lg"
                >
                  {paused ? t("wo.workPaused") : t("wo.workInProgress")}
                </Button>
                <Button
                  data-testid="work-order-pause-button"
                  disabled={busy}
                  onClick={() => { setPausing(true); act("toggle_pause").finally(() => setPausing(false)); }}
                  className={`h-20 whitespace-normal font-heading text-base font-bold tracking-wide text-white transition-colors duration-200 sm:text-lg ${
                    paused ? "bg-orange-600 hover:bg-orange-700" : "bg-orange-500 hover:bg-orange-600"
                  }`}
                >
                  {busy ? (
                    <Loader2 className="h-5 w-5 animate-spin" />
                  ) : paused ? (
                    <span className="flex items-center gap-1.5"><Play className="h-5 w-5" /> {t("wo.resume")}</span>
                  ) : (
                    <span className="flex items-center gap-1.5"><Pause className="h-5 w-5" /> {t("wo.pauseBtn")}</span>
                  )}
                </Button>
              </div>
            )}

            {(working || paused) && (
              <Button
                data-testid="work-order-pause-job"
                disabled={busy}
                onClick={() => act("pause_job")}
                className="h-14 w-full bg-amber-700 font-heading text-base font-bold tracking-wide text-white transition-colors duration-200 hover:bg-amber-800"
              >
                <Pause className="mr-1.5 h-5 w-5" /> {t("wo.pauseJob")}
              </Button>
            )}
            {jobPaused && (
              <p className="rounded-md border border-orange-200 bg-orange-50 p-3 text-sm font-medium text-orange-900" data-testid="work-order-paused-note">
                {t("wo.pausedNote")}
              </p>
            )}

            {(working || paused) && (
              <Button
                data-testid="work-order-finish-work"
                disabled={busy}
                onClick={() => act("finish_work")}
                variant="outline"
                className="h-14 w-full border-slate-300 bg-white font-heading text-base font-bold tracking-wide text-slate-900 transition-colors duration-200 hover:bg-slate-100"
              >
                {t("wo.finishWork")}
              </Button>
            )}
          </div>
        ) : ro ? (
          <div
            className="mt-4 flex items-center gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm font-medium text-amber-900"
            data-testid="work-order-readonly-note"
          >
            <CheckCircle2 className="h-4 w-4" /> Visningsläge – tidsregistrering är avstängd i demot
          </div>
        ) : (
          <div
            className="mt-4 flex items-center gap-2 rounded-md border border-green-200 bg-green-50 p-3 text-sm font-medium text-green-800"
            data-testid="work-order-locked"
          >
            <CheckCircle2 className="h-4 w-4" /> {t("wo.lockedNote")}
          </div>
        )}
      </div>
      )}

      {/* Totaler */}
      {!openJob && (
      <div className="mt-3 rounded-lg border border-slate-200 bg-white p-4" data-testid="work-order-totals">
        <h2 className="text-xl font-semibold text-slate-800">{t("wo.timeOnOrder")}</h2>
        <Row label={t("wo.totalTravel")} value={fmtDuration(totals.travel_seconds)} testid="total-travel" />
        <Row label={t("wo.totalWork")} value={fmtDuration(totals.work_seconds)} testid="total-work" />
        <Row label={t("wo.totalTime")} value={fmtDuration(totals.total_seconds)} testid="total-time" />

        <h3 className="mt-3 flex items-center gap-1.5 text-xs font-medium uppercase tracking-[0.05em] text-slate-500">
          <Users2 className="h-3.5 w-3.5" /> {t("wo.perTech")}
        </h3>
        {(summary?.per_user || []).length === 0 && (
          <p className="mt-1 text-sm text-slate-500" data-testid="work-order-per-user-empty">
            {t("wo.noTimeYet")}
          </p>
        )}
        {(summary?.per_user || []).map((u) => (
          <div
            key={u.user_id}
            data-testid={`work-order-user-time-${u.user_id}`}
            className="border-b border-slate-100 py-2 last:border-0"
          >
            <div className="flex flex-wrap items-center gap-2">
              <span className="min-w-0 flex-1 text-sm font-semibold text-slate-900">{u.user_name}</span>
              <span className="text-xs text-slate-500">{u.status_label}</span>
              <span className="text-sm font-medium text-slate-900">{fmtDuration(u.work_seconds)}</span>
              <span className="text-sm text-slate-500">{t("common.travel")} {fmtDuration(u.travel_seconds)}</span>
              <span className="text-sm text-slate-500">{t("common.pause")} {fmtDuration(u.pause_seconds)}</span>
            </div>
            {(u.manual_entries || []).map((e) => (
              <div
                key={e.id}
                data-testid={`work-order-manual-entry-${e.id}`}
                className="mt-1 flex flex-wrap items-center gap-2 pl-3 text-sm text-slate-600"
              >
                <span className="text-slate-700">{e.date}</span>
                <span className={`rounded border px-1.5 py-0.5 text-[11px] font-semibold ${
                  e.kind === "travel"
                    ? "border-sky-200 bg-sky-50 text-sky-800"
                    : "border-emerald-200 bg-emerald-50 text-emerald-800"
                }`}>
                  {e.kind === "travel" ? "Restid" : "Arbetstid"}
                </span>
                {e.start && e.end && <span>{e.start}–{e.end}</span>}
                <span className="font-medium text-slate-900">{fmtDuration(e.seconds)}</span>
                <span className="text-xs italic text-slate-400">Manuellt registrerad</span>
                {e.note && <span className="min-w-0 flex-1 truncate text-slate-500">{e.note}</span>}
              </div>
            ))}
          </div>
        ))}
      </div>
      )}

      {/* Sammanställning */}
      {!openJob && (done || me.work_end) && (
        <div className="mt-3 rounded-lg border border-slate-200 bg-white p-4" data-testid="work-order-summary">
          <h2 className="text-xl font-semibold text-slate-800">{t("wo.summary")}</h2>
          <h3 className="mt-3 text-xs font-medium uppercase tracking-[0.05em] text-slate-500">{t("wo.tripThere")}</h3>
          <Row label={t("wo.startTime")} value={timeOf(me.travel_start)} />
          <Row label={t("wo.arrival")} value={timeOf(me.arrived_at)} />
          <Row label={t("wo.totalTravel")} value={fmtDuration(me.travel_out_seconds || 0)} testid="summary-travel-out" />

          <h3 className="mt-4 text-xs font-medium uppercase tracking-[0.05em] text-slate-500">{t("wo.work")}</h3>
          <Row label={t("wo.startTime")} value={timeOf(me.work_start)} />
          <Row label={t("wo.endTime")} value={timeOf(me.work_end)} />
          <Row label={t("wo.totalWork")} value={fmtDuration(me.work_seconds || 0)} testid="summary-work" />

          {(me.sessions || []).length > 0 && (
            <div data-testid="work-order-sessions">
              <h3 className="mt-4 text-xs font-medium uppercase tracking-[0.05em] text-slate-500">{t("wo.sessions")}</h3>
              {(me.sessions || []).map((s, i) => (
                <Row
                  key={`${s.start || "s"}-${s.end || i}`}
                  testid={`work-order-session-${i}`}
                  label={`${t("wo.sessionN", { n: i + 1 })} · ${(s.start || "").slice(0, 10)}`}
                  value={`${timeOf(s.start)} – ${timeOf(s.end)} (${fmtDuration(s.seconds || 0)})`}
                />
              ))}
            </div>
          )}

          <h3 className="mt-4 text-xs font-medium uppercase tracking-[0.05em] text-slate-500">{t("wo.pauses")}</h3>          <Row label={t("wo.pauseCount")} value={(me.pauses || []).length} testid="summary-pause-count" />
          <Row label={t("wo.totalPause")} value={fmtDuration(me.pause_seconds || 0)} testid="summary-pause" />
          {(me.pauses || []).map((p, i) => (
            <Row key={`${p.start || "p"}-${p.end || i}`} label={t("wo.pauseN", { n: i + 1 })} value={`${timeOf(p.start)} – ${timeOf(p.end)} (${fmtDuration(p.seconds || 0)})`} />
          ))}

          <h3 className="mt-4 text-xs font-medium uppercase tracking-[0.05em] text-slate-500">{t("wo.tripHome")}</h3>
          <Row label={t("wo.startTime")} value={timeOf(me.travel_home_start)} />
          <Row label={t("wo.endTime")} value={timeOf(me.completed_at)} />
          <Row label={t("wo.totalTravel")} value={fmtDuration(me.travel_home_seconds || 0)} testid="summary-travel-home" />

          <div className="mt-4 rounded-md bg-slate-900 p-3 text-white">
            <div className="flex items-center justify-between py-1">
              <span className="text-xs font-medium uppercase tracking-[0.05em] text-slate-300">{t("wo.totalDistanceRT")}</span>
              <span className="text-sm font-medium" data-testid="summary-distance">
                {job.distance_km != null ? `${(job.distance_km / 5).toFixed(1)} mil` : "–"}
              </span>
            </div>
            {job.distance_km != null && (
              <div className="flex items-center justify-between py-1">
                <span className="text-xs font-medium uppercase tracking-[0.05em] text-slate-300">{t("wo.ofWhichOneWay")}</span>
                <span className="text-sm font-medium" data-testid="summary-distance-single">
                  {(job.distance_km / 10).toFixed(1)} mil
                </span>
              </div>
            )}
            <div className="flex items-center justify-between py-1">
              <span className="text-xs font-medium uppercase tracking-[0.05em] text-slate-300">{t("wo.totalTravel")}</span>
              <span className="text-sm font-medium" data-testid="summary-travel-total">{fmtDuration(totals.travel_seconds)}</span>
            </div>
            <div className="flex items-center justify-between py-1">
              <span className="text-xs font-medium uppercase tracking-[0.05em] text-slate-300">{t("wo.totalWork")}</span>
              <span className="text-sm font-medium">{fmtDuration(totals.work_seconds)}</span>
            </div>
            <div className="flex items-center justify-between pt-1">
              <span className="text-xs font-medium uppercase tracking-[0.05em] text-slate-300">{t("wo.totalTime")}</span>
              <span className="font-heading text-lg font-bold" data-testid="summary-total">{fmtDuration(totals.total_seconds)}</span>
            </div>
          </div>
        </div>
      )}

      {/* Material, reservdelar och annat – ingår inte i uthyrning */}
      {!openJob && (
      <div className="mt-3 space-y-3">
        <LineItems label={t("wo.materials")} testid="wo-materials" category="material" readOnly={!leader} items={job.materials} onChange={(v) => saveItems("materials", v)} />
        <LineItems label={t("wo.spareParts")} testid="wo-spare-parts" category="spare_part" readOnly={!leader} items={job.spare_parts} onChange={(v) => saveItems("spare_parts", v)} />
        <LineItems label={t("wo.other")} testid="wo-others" category="other" readOnly={!leader} items={job.others} onChange={(v) => saveItems("others", v)} />
        <p className="text-right text-sm font-semibold text-slate-800" data-testid="wo-items-total">
          {t("wo.itemsTotal")}{" "}
          {(sumItems(job.materials) + sumItems(job.spare_parts) + sumItems(job.others)).toFixed(2)} kr
        </p>
        {!leader && (
          <p className="text-xs text-slate-500" data-testid="wo-items-readonly">
            {t("wo.readOnlyNote")}
          </p>
        )}
      </div>
      )}

      {/* Manuella tidsregistreringar (kundunik funktion – uthyrning använder den alltid) */}
      {(openJob || (!openJob && company?.features?.manual_time)) && (
        <TimeEntriesPanel
          job={job}
          staff={staff}
          onSaved={load}
          readOnly={ro || invoiced}
          workOnly={openJob}
        />
      )}

      {/* Egen tidsrättning (kundunik funktion) */}
      {!openJob && !invoiced && company?.features?.self_time_edit &&
        (job.assigned_to || []).includes(user?.id) && (
          <MyTimePanel job={job} me={me} onSaved={load} readOnly={ro} />
        )}

      {/* Avslutad order: lås upp, redigera, historik */}
      {(leader || finished || (job.edit_history || []).length > 0) && (
        <FinishedEditPanel job={job} summary={summary} staff={staff} leader={leader} onSaved={load} simple={openJob} />
      )}

      {/* Fakturaunderlag */}
      <InvoicePanel jobId={id} key={`inv-${job.edit_history?.length || 0}-${job.distance_km}-${job.work_seconds}`} />

      {/* Fritext */}
      <div className="mt-3 rounded-lg border border-slate-200 bg-white p-4" data-testid="work-order-notes">
        <h2 className="text-xl font-semibold text-slate-800">{t("wo.notes")}</h2>
        <div className="mt-2 space-y-2">
          {(job.notes || []).length === 0 && (
            <p className="text-sm text-slate-500" data-testid="work-order-notes-empty">{t("wo.notesEmpty")}</p>
          )}
          {(job.notes || []).map((n) => (
            <div key={n.id} data-testid={`work-order-note-${n.id}`} className="rounded-md border border-slate-200 bg-slate-50 p-2.5">
              <div className="flex items-start gap-2">
                <p className="min-w-0 flex-1 text-sm text-slate-800">{n.text}</p>
                {!ro && (
                <button
                  data-testid={`work-order-note-delete-${n.id}`}
                  onClick={() => removeNote(n.id)}
                  className="rounded p-1 text-red-600 transition-colors duration-200 hover:bg-red-100"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>)}
              </div>
              <p className="mt-1 text-xs text-slate-500">
                {n.user_name || "–"} · {(n.created_at || "").slice(0, 16).replace("T", " ")} ·{" "}
                {n.phase === "creation" ? t("wo.atCreation") : t("wo.duringWork")}
              </p>
            </div>
          ))}
        </div>
        {!ro && (<><Textarea
          data-testid="work-order-note-input"
          value={noteText}
          onChange={(e) => setNoteText(e.target.value)}
          placeholder={t("wo.notePlaceholder")}
          rows={2}
          className="mt-2"
        />
        <Button
          data-testid="work-order-note-add"
          onClick={addNote}
          className="mt-2 h-10 bg-blue-600 text-white transition-colors duration-200 hover:bg-blue-700"
        >
          <Plus className="mr-1.5 h-4 w-4" /> {t("wo.addNote")}
        </Button></>)}
      </div>

      {/* Foton */}
      <div className="mt-3">
        <PhotoGrid
          photos={photos}
          files={files}
          jobId={id}
          token={token}
          busy={busy}
          fileRef={fileRef}
          onFile={upload}
          imageRef={imageRef}
          onImage={uploadFromGallery}
          docRef={docRef}
          onDoc={uploadDocs}
          onDocDelete={removeDoc}
          onAdd={() => fileRef.current?.click()}
          onDelete={removePhoto}
          readOnly={ro}
        />
      </div>

      {/* Färdig för fakturering – egen funktion längst ner */}
      {!readyToInvoice && !ro ? (
        <div className="mt-3 rounded-lg border border-teal-200 bg-teal-50/60 p-4" data-testid="ready-to-invoice-panel">
          <h2 className="text-xl font-semibold text-slate-800">{t("wo.readyTitle")}</h2>
          <p className="mt-1 text-sm text-slate-600">{t("wo.readyHint")}</p>
          <Button
            data-testid="work-order-ready-to-invoice"
            disabled={busy}
            onClick={markReady}
            className="mt-3 h-14 w-full bg-teal-700 font-heading text-base font-bold tracking-wide text-white transition-colors duration-200 hover:bg-teal-800"
          >
            <CheckCircle2 className="mr-1.5 h-5 w-5" /> {t("wo.readyBtn")}
          </Button>
        </div>
      ) : readyToInvoice ? (
        <div
          className="mt-3 flex flex-wrap items-center gap-2 rounded-lg border border-teal-300 bg-teal-50 p-4 text-sm font-semibold text-teal-900"
          data-testid="work-order-ready-badge"
        >
          <CheckCircle2 className="h-4 w-4" /> {t("wo.readyDone")}
          <span className="font-normal text-teal-800">
            {job.ready_to_invoice_by} · {dateTimeOf(job.ready_to_invoice_at)}
          </span>
        </div>
      ) : null}

      {/* Fakturerad – sista steget i arbetsflödet */}
      {invoiced ? (
        <div
          className="mt-3 flex flex-wrap items-center gap-2 rounded-lg border border-indigo-300 bg-indigo-50 p-4 text-sm font-semibold text-indigo-900"
          data-testid="work-order-invoiced-badge"
        >
          <Receipt className="h-4 w-4" /> {t("wo.invoicedDone")}: {dateOf(job.invoiced_at)}
          {job.invoiced_by && <span className="font-normal text-indigo-800">· {job.invoiced_by}</span>}
        </div>
      ) : readyToInvoice && leader ? (
        <div className="mt-3 rounded-lg border border-indigo-200 bg-indigo-50/60 p-4" data-testid="invoiced-panel">
          <h2 className="text-xl font-semibold text-slate-800">{t("wo.invoicedTitle")}</h2>
          <p className="mt-1 text-sm text-slate-600">{t("wo.invoicedHint")}</p>
          <Button
            data-testid="work-order-invoiced"
            disabled={busy}
            onClick={() => setInvoiceConfirm(true)}
            className="mt-3 h-14 w-full bg-indigo-700 font-heading text-base font-bold tracking-wide text-white transition-colors duration-200 hover:bg-indigo-800"
          >
            <Receipt className="mr-1.5 h-5 w-5" /> {t("wo.invoicedBtn")}
          </Button>
        </div>
      ) : null}

      <AlertDialog open={invoiceConfirm} onOpenChange={setInvoiceConfirm}>
        <AlertDialogContent className="bg-white" data-testid="invoiced-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{t("wo.invoicedConfirm")}</AlertDialogTitle>
            <AlertDialogDescription>{t("wo.invoicedHint")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="invoiced-confirm-cancel">{t("wo.invoicedCancel")}</AlertDialogCancel>
            <AlertDialogAction
              data-testid="invoiced-confirm-ok"
              disabled={busy}
              onClick={(e) => { e.preventDefault(); markInvoiced(); }}
              className="bg-indigo-700 text-white hover:bg-indigo-800"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : t("wo.invoicedOk")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Total längst ned – samma siffror som i sammanställningen ovan */}
      <div className="mt-3 rounded-lg border border-slate-900 bg-slate-900 p-4 text-white" data-testid="work-order-grand-total">
        <h2 className="text-xs font-medium uppercase tracking-[0.05em] text-slate-300">
          Totalt på arbetsordern (inkl. manuellt registrerad tid)
        </h2>
        <div className="mt-2 flex items-center justify-between py-1">
          <span className="text-sm text-slate-300">{t("wo.totalWork")}</span>
          <span className="text-sm font-medium" data-testid="grand-total-work">{fmtDuration(totals.work_seconds)}</span>
        </div>
        <div className="flex items-center justify-between py-1">
          <span className="text-sm text-slate-300">{t("wo.totalTravel")}</span>
          <span className="text-sm font-medium" data-testid="grand-total-travel">{fmtDuration(totals.travel_seconds)}</span>
        </div>
        <div className="mt-1 flex items-center justify-between border-t border-slate-700 pt-2">
          <span className="text-sm font-semibold uppercase tracking-[0.05em]">Total arbetad tid</span>
          <span className="font-heading text-2xl font-bold" data-testid="grand-total-time">{fmtDuration(totals.total_seconds)}</span>
        </div>
      </div>

      {/* PDF / utskrift */}
      <div className="mt-3 flex flex-wrap gap-2">
        <Button
          data-testid="work-order-pdf-button"
          onClick={openPdf}
          className="h-11 bg-blue-600 text-white transition-colors duration-200 hover:bg-blue-700"
        >
          <FileDown className="mr-1.5 h-4 w-4" /> {t("common.openPdf")}
        </Button>
        <Button
          data-testid="work-order-print-button"
          onClick={() => window.print()}
          variant="outline"
          className="h-11 bg-white transition-colors duration-200 hover:bg-slate-100"
        >
          <Printer className="mr-1.5 h-4 w-4" /> {t("common.print")}
        </Button>
        {leader && (
          <Button
            data-testid="work-order-edit-button"
            onClick={() => setEditOpen(true)}
            variant="outline"
            className="h-11 bg-white transition-colors duration-200 hover:bg-slate-100"
          >
            {t("wo.editOrder")}
          </Button>
        )}
      </div>

      <JobDialog open={editOpen} onOpenChange={setEditOpen} job={job} staff={staff} onSaved={load} />
    </div>
  );
}
